export const CONTENT_PAGE_SEGMENT = "content";

export const LEGACY_PAGE_SEGMENTS = {
  task: "tasks",
  stories: CONTENT_PAGE_SEGMENT,
};

const STUDIO_PATH_PREFIXES = ["build", "builder"];

const PAGE_SEGMENT_INDEX = 3;

export const SETTINGS_SEGMENT = "settings";

export const PAGES_UNDER_SETTINGS = ["features", "apps"];

export const MOVED_ROUTE_PREFIXES = {
  "theme": "branding/theme",
  "settings/features/programs": "content/programs",
  "content/programs/templates": "content/templates",
};

// Moves chain: settings/features/programs became content/programs, which has now
// moved again. Resolving a single hop would leave the oldest urls pointing at a
// route that no longer exists, so the rules are applied until they settle — with
// a bound, because a mistyped pair that points at each other would otherwise
// spin forever with nothing to show for it.
const MAX_ROUTE_HOPS = 8;

export const MOVED_ROUTES = {
  "settings/features/stories": "content/settings",
  "settings/features/stories/settings": "content/settings",
  "settings/features/stories/pages-components": "content/story-types",
};

export function canonicalPageSegment(page) {
  return LEGACY_PAGE_SEGMENTS[page] || page;
}

const PAGES_OWNED_BY_ANOTHER_SECTION = {
  tasks: "home",
  workflows: "home",
  chat: "home",
  overview: "home",
  setup: "home",
  features: SETTINGS_SEGMENT,
  apps: SETTINGS_SEGMENT,
  theme: "pages",
  branding: "pages",
  story: "content",
  components: "pages",
  designs: "pages",
  concepts: "pages",
  library: "pages",
};

const DEFAULT_SECTION = "home";

export function pageSection(page) {
  if (!page) {
    return DEFAULT_SECTION;
  }
  return PAGES_OWNED_BY_ANOTHER_SECTION[page] || page;
}

export function pageLivesUnderSettings(page) {
  return PAGES_UNDER_SETTINGS.includes(page);
}

export function pathSegmentsForPage(page) {
  if (!page) {
    return [];
  }
  return pageLivesUnderSettings(page) ? [SETTINGS_SEGMENT, page] : [page];
}

export function canonicalStudioPath(pathname) {
  const parts = (pathname || "/").split("/");
  if (!STUDIO_PATH_PREFIXES.includes(parts[1])) {
    return pathname;
  }

  const page = parts[PAGE_SEGMENT_INDEX];

  if (LEGACY_PAGE_SEGMENTS[page]) {
    parts[PAGE_SEGMENT_INDEX] = LEGACY_PAGE_SEGMENTS[page];
  } else if (pageLivesUnderSettings(page)) {
    parts.splice(PAGE_SEGMENT_INDEX, 0, SETTINGS_SEGMENT);
  }

  const head = parts.slice(0, PAGE_SEGMENT_INDEX);
  const tail = parts.slice(PAGE_SEGMENT_INDEX);

  const moved = MOVED_ROUTES[tail.join("/")];
  if (moved) {
    return [...head, ...moved.split("/")].join("/");
  }

  return [...head, ...followMoves(tail)].join("/");
}

function followMoves(tail) {
  let current = tail;
  for (let hop = 0; hop < MAX_ROUTE_HOPS; hop += 1) {
    const next = applyOneMove(current);
    if (!next) {
      return current;
    }
    current = next;
  }
  throw new Error(
    `[studio-routes] "${tail.join("/")}" is still moving after ${MAX_ROUTE_HOPS} hops, so two`
    + " MOVED_ROUTE_PREFIXES rules point at each other. Fix the pair rather than raising the bound.",
  );
}

function applyOneMove(tail) {
  const movedPrefix = Object.keys(MOVED_ROUTE_PREFIXES)
      .map(from => ({ from: from.split("/"), to: MOVED_ROUTE_PREFIXES[from] }))
      .find(candidate => candidate.from.every((segment, at) => tail[at] === segment));
  if (!movedPrefix) {
    return null;
  }
  return [...movedPrefix.to.split("/"), ...tail.slice(movedPrefix.from.length)];
}
